import React, { useEffect, useState, useMemo } from "react";
import { Card } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { ChevronRight, RefreshCw } from "lucide-react";
import CommonModal from "../Modals/CommonModal";
import CompanyRegistration from "../CompanyProfile/CompanyProfile";
import { ScrollArea } from "../../../components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import { useInvitationManagement } from "../../Services/InvitationManagement";
import { useUserContext } from "../../../Contexts/UserProvider";
import { useUIContext } from "../../../Contexts/UIProvider";
import { InvitationInfo } from "../Invitations/interface";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { Search, Plus, MoreHorizontal, Loader2 } from "lucide-react";
import InvitationManagement from "../Invitations/InvitationManagement";
import { useTenantManagement } from "../../Services/TenantManagement";
import { useNavigate } from "react-router-dom";
import { RegistrationStatus, Status } from "../../../Utilities/enums";
import DataTable from "react-data-table-component";
import { customStyles } from "../Common/DataTableStyles";
import TenantDetailsSheet from "./TenantDetailsSheet";

const AccountDashboard = () => {
  const [isShow, setIsShow] = useState(false);
  const [invitations, setInvitations] = useState<InvitationInfo[]>([]);
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const [isDropDownOpen, setIsDropdownOpen] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState({
    selectedInvitations: null,
    currentView: null,
  });
  const [selectedTenant, setSelectedTenant] = useState<any>(null);
  const [isTenantDetailsOpen, setIsTenantDetailsOpen] = useState(false);

  const { getInvitations, declineInvitation } = useInvitationManagement();
  const { getTenantsForUsers } = useTenantManagement();
  const { user, setUser } = useUserContext();
  const { accountDashboardVersion, notifyAccountDashboardRefresh } = useUIContext();
  const navigate = useNavigate();

  const handleShiftTenant = async (tenant) => {
    setUser({ tenant: tenant?.code });
    navigate(`/app/${tenant?.code}/dashboard`);
    window.location.reload();
  };

  const handleViewTenantDetails = (tenant: any) => {
    setSelectedTenant(tenant);
    setIsTenantDetailsOpen(true);
  };

  const invitationColumns = useMemo(() => [
    {
      name: "Invited From",
      selector: (row) => row.invitedTenantFrom,
      sortable: true,
      grow: 1,
    },
    {
      name: "Invited By",
      selector: (row) => row.invitedUserFrom,
      sortable: true,
      grow: 1,
    },
    {
      name: "Invited To",
      selector: (row) => row.invitedTo,
      sortable: true,
      grow: 1,
    },
    {
      name: "Partner Type",
      selector: (row) => row.tenantType,
      sortable: true,
      grow: 1,
    },
    {
      name: "Invited Date",
      selector: (row) => "2025-02-26", // Keeping hardcoded date as in original
      sortable: true,
      grow: 1,
    },
    {
      name: "Actions",
      cell: (row, index) => (
        <div className="flex justify-center w-full">
          <DropdownMenu modal={false} onOpenChange={(isOpen) => setIsDropdownOpen(isOpen ? index : null)} open={isDropDownOpen === index}>
            <DropdownMenuTrigger>
              <MoreHorizontal className="h-5 w-5 text-gray-500" />
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => handleViewDetails(row)}>
                View
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleDecline(row)}>
                Decline
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      center: true,
    },
  ], [isDropDownOpen]);

  const tenantColumns = useMemo(() => [
    {
      name: "Tenant Name",
      selector: (row) => row.name,
      sortable: true,
      grow: 1,
    },
    {
      name: "Address",
      selector: (row) => row.address,
      sortable: true,
      grow: 1,
    },
    {
      name: "City",
      selector: (row) => row.city,
      sortable: true,
      grow: 1,
    },
    {
      name: "Approval Status",
      selector: (row) => row.registrationStatus,
      sortable: true,
      grow: 1,
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="flex justify-center w-full">
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger>
              <MoreHorizontal className="h-5 w-5 text-gray-500" />
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem
                onClick={() => handleShiftTenant(row)}
                disabled={row.registrationStatus === Status.PENDING || row.registrationStatus === Status.REJECTED}
              >
                View
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleViewTenantDetails(row)}
              >
                View Details
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      center: true,
    },
  ], [handleViewTenantDetails]);

  useEffect(() => {
    if (!user?.email) return;

    let cancelled = false;

    const loadDashboard = async () => {
      setIsLoading(true);
      try {
        const [invitationsResponse, tenantsResponse] = await Promise.all([
          getInvitations(user.email),
          getTenantsForUsers(),
        ]);

        if (cancelled) return;

        const mappedInvitations: InvitationInfo[] = (invitationsResponse || []).map(
          (invitation: any) => ({
            id: invitation.id,
            tenantType: invitation.userType,
            invitedTo: invitation.invitedTo,
            invitedUserFrom: invitation.invitedFromUserName,
            invitedTenantFrom: invitation.invitedFromTenantName,
            logo: invitation.logo,
          })
        );

        const mappedTenants = (tenantsResponse || []).map((tenantItem) => ({
          ...tenantItem,
          registrationStatus: RegistrationStatus[tenantItem.registrationStatus],
          country: tenantItem.country || "Sri Lanka",
          companyType: tenantItem.companyType || "Limited Liability Company",
          industry: tenantItem.industry || "Engineering",
        }));

        setInvitations(mappedInvitations);
        setTenants(mappedTenants);
      } catch (error) {
        if (!cancelled) {
          console.log("Dashboard load error:", error);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadDashboard();

    return () => {
      cancelled = true;
    };
  }, [user?.email, accountDashboardVersion]);

  const handleCreateCompany = () => {
    setIsShow(!isShow);
  };

  const handleViewDetails = (values) => {
    console.log("Selected Values : ", values);
    setSelectedItem({
      selectedInvitations: values,
      currentView: values?.tenantType === 'Partner' || values?.tenantType === 'ReSeller' ? "companies" : "user_comfirmation",
    });
    setIsOpenDialog(true);
    setIsDropdownOpen(null);
  };



  const handleDecline = (invitation) => {
    declineInvitation(invitation?.id)
      .then((res) => {
        console.log("Delete  : ", res);
        notifyAccountDashboardRefresh();
      })
      .catch((err) => {
        console.log("Delete Error : ", err);
      });
  };

  return (
    <div className="w-full max-w-6xl mx-auto p-6 space-y-6">
      {/* Welcome Section */}
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold">Welcome to Elystrion!</h1>
        <p>
          Let's get your business blockchain-ready.
        </p>
      </div>

      {invitations.length !== 0 && (
        <Card className="p-6">
          <div className="space-y-4">
            <p className="text-primary-text text-md font-semibold">
              You have invitations from these Companies and Users for join with
              them
            </p>

            <div className="bg-black rounded-lg shadow overflow-hidden border border-gray-800">
              <DataTable
                columns={invitationColumns}
                data={invitations}
                pagination
                paginationPerPage={10}
                paginationRowsPerPageOptions={[10, 15, 20]}
                fixedHeader
                fixedHeaderScrollHeight="300px"
                progressPending={isLoading}
                progressComponent={
                  <div className="flex items-center justify-center p-10">
                    <Loader2 className="animate-spin h-8 w-8 text-white" />
                  </div>
                }
                noDataComponent={
                  <div className="text-center py-10 text-gray-400">
                    No rows to show
                  </div>
                }
                customStyles={customStyles}
                highlightOnHover
                pointerOnHover
                responsive
              />
            </div>
          </div>
        </Card>
      )}

      {/* Connect Store Card */}
      <Card className="p-6">
        <div className="space-y-4">
          <h2 className="text-primary-text text-xl font-semibold">
            Connect your store to start tracking authentic sales
          </h2>
          <p className="text-secondary-text">
            Link your Shopify store to begin recording transactions on the
            blockchain. Every sale becomes an authenticity proof for your
            customers.
          </p>
          <div className="space-y-4">
            <Button variant="link" className="h-auto p-0 text-primary-text">
              Learn more
            </Button>
            <div>
              <Button>
                Connect my store
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Bottom Grid */}
      <div className="grid md:grid-cols-1 gap-6">
        {/* Invite Resellers Card */}
        <Card className="p-6">
          <div className="space-y-4">
            <h2 className="text-primary-text text-xl font-semibold">My Orgnizations</h2>
            <div className="bg-black rounded-lg shadow overflow-hidden border border-gray-800">
              <DataTable
                columns={tenantColumns}
                data={tenants}
                pagination
                paginationPerPage={10}
                paginationRowsPerPageOptions={[10, 15, 20]}
                fixedHeader
                fixedHeaderScrollHeight="300px"
                progressPending={isLoading}
                progressComponent={
                  <div className="flex items-center justify-center p-10">
                    <Loader2 className="animate-spin h-8 w-8 text-white" />
                  </div>
                }
                noDataComponent={
                  <div className="text-center py-10 text-gray-400">
                    No rows to show
                  </div>
                }
                customStyles={customStyles}
                highlightOnHover
                pointerOnHover
                responsive
              />
            </div>
            <Button variant="outline" onClick={handleCreateCompany} className="dark-button">Register Orgnization</Button>
          </div>
        </Card>

        {/* Need Help Card */}
        <Card className="p-6">
          <div className="space-y-4">
            <h2 className="text-primary-text text-xl font-semibold">Need Help?</h2>
            <p className="text-secondary-text">
              Explore our guides, FAQs, or reach out to our support team for
              assistance with setup or using the platform.
            </p>
            <ul className="space-y-2">
              <li>
                <Button variant="link" className="h-auto p-0 text-primary-text">
                  Setup guide <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </li>
              <li>
                <Button variant="link" className="h-auto p-0 text-primary-text">
                  FAQs <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </li>
              <li>
                <Button variant="link" className="h-auto p-0 text-primary-text">
                  Contact support <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </li>
            </ul>
          </div>
        </Card>
      </div>

      <CompanyRegistration
        open={isShow}
        onOpenChange={setIsShow}
        setIsContentLoad={true}
        setIsShow={setIsShow}
        setIsDialogOpen={() => { }}
        setIsRegisterTenant={() => { }}
        onSuccessCallBack={notifyAccountDashboardRefresh}
      ></CompanyRegistration>

      <InvitationManagement
        isInvitation={isOpenDialog}
        setIsInvitation={setIsOpenDialog}
        selectedInvitations={selectedItem?.selectedInvitations}
        currentView={selectedItem?.currentView}
        onSuccessCallBack={notifyAccountDashboardRefresh}
      ></InvitationManagement>

      <TenantDetailsSheet
        tenant={selectedTenant}
        open={isTenantDetailsOpen}
        onOpenChange={setIsTenantDetailsOpen}
      />
    </div>
  );
};

export default AccountDashboard;
